import { useMutation } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { queryClient } from 'v2/configs/rq-setup'
import { billingService } from 'v2/shared/services/billing-service'
import { hodorService } from 'v2/shared/services/hodor-service'
import { settingsService } from 'v2/shared/services/settings-service'
import { billingQueryKeys, getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { ChangePlanPayload } from 'v2/types/api/auth'
import type { RechargePayload } from 'v2/types/api/billing'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { errorToast, successToast } from 'v2/utils/toasts'
import { PaymentMethod } from './billing.config'
import { useBillingRechargeDetailsQuery, useConversionRate } from './queries'
import {
  type AddBankAccountPayload,
  type AddCardForm,
  type AddSchedulePayload,
  type AutoRechargeLimitForm,
  type BillingInformationForm,
  CardState,
  type DeleteBankAccountPayload,
  type DeleteCardPayload,
  type RAddCard,
  type ToggleACHPayload,
  type UpdateAuditLogsPayload,
  type UpdateBillingAlertsPayload,
  type UpdateBillingEmailPayload,
  type UpdateDefaultCardPayload,
  type UpdateSchedulePayload,
  type VerifyBankAccountPayload,
} from './types'
import { useIsCashfreeProvider } from './use-billing-provider'
import { getAccountId, trackPaymentFailed } from './utils'

export const useBillingInformationMutation = () => {
  const accountId = getAccountId()
  return useMutation({
    mutationFn: async (data: BillingInformationForm) => {
      const response = await billingService.updateBillingInformation(data, accountId)
      return response
    },
  })
}

export const useUpdateOrgNameMutation = () => {
  return useMutation({
    mutationFn: async (data: { name: string }) => {
      const response = await settingsService.updateOrgName({ name: data.name })
      return response.data
    },
    onSuccess: (_data, variables) => {
      try {
        const auth = JSON.parse(localStorage.getItem('auth') || '{}')
        auth.organization_name = variables.name
        localStorage.setItem('auth', JSON.stringify(auth))
      } catch {
        // localStorage corrupted — skip local update, query invalidation will refresh
      }
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().profile })
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().listOrganizations })
    },
  })
}

export const useAddCardMutation = () => {
  const accountId = getAccountId()
  return useMutation({
    mutationFn: async (data: AddCardForm) => {
      const response = await billingService.addCard(data, accountId)
      return response
    },
  })
}

// Posts to `/cashfree/pay` (save_card + min recharge_choice) for INR-Cashfree
// orgs; otherwise falls through to Stripe addCard. Cashfree response is
// reshaped to RAddCard so the existing redirect_url-based 3DS handler stays
// unchanged.
export const useAddCardOrCashfreePayMutation = () => {
  const accountId = getAccountId()
  const isCashfree = useIsCashfreeProvider()
  const { data: rechargeDetails } = useBillingRechargeDetailsQuery()

  return useMutation({
    mutationFn: async (form: AddCardForm): Promise<RAddCard> => {
      if (!isCashfree) {
        return billingService.addCard(form, accountId)
      }
      const minAmount = rechargeDetails?.recharge_choices?.[0] ?? 10
      const response = await billingService.cashfreePay({
        account_id: accountId,
        amount: minAmount,
        product_type: 'plivo',
        return_url: `${window.location.origin}/payment/callback?method=card`,
        save_card: true,
        card: {
          card_number: form.number.replace(/\D/g, ''),
          card_holder_name: form.name,
          card_expiry_mm: form.month,
          card_expiry_yy: form.year,
          card_cvv: form.cvc,
        },
      })
      return {
        success: response.success,
        uuid: response.uuid ?? '',
        message: response.message,
        redirect_url: response.payment_url,
        error: '',
        card_type: '',
        card_name: '',
        card_country: '',
      }
    },
  })
}

export const useUpdateBalanceAlertsMutation = () => {
  const accountId = getAccountId()
  const { conversionRate } = useConversionRate()
  return useMutation({
    mutationFn: async (data: {
      balanceAlertPayload: UpdateBillingAlertsPayload
      billingEmailPayload: UpdateBillingEmailPayload
    }) => {
      const [balanceAlertResponse, billingEmailResponse] = await Promise.all([
        billingService.updateBillingAlerts(data.balanceAlertPayload, accountId),
        billingService.updateBillingEmail(data.billingEmailPayload, accountId),
      ])
      return {
        balanceAlertResponse,
        billingEmailResponse,
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().balanceAlerts(accountId, conversionRate),
      })
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().billingEmail(accountId),
      })
      successToast({
        title: 'Success',
        description: 'Balance alerts updated successfully',
      })
      track(AnalyticsEvents.BALANCE_ALERT_CONFIGURED, {
        alert_count: variables.balanceAlertPayload.threshold_values.length,
        email_enabled: variables.balanceAlertPayload.send_email,
      })
    },
    onError: () => {
      errorToast({
        title: 'Error',
        description: 'Unable to update some of the balance alerts',
      })
    },
  })
}

export const useSetupAutoRechargeMutation = () => {
  const accountId = getAccountId()
  const { conversionRate } = useConversionRate()
  return useMutation({
    mutationFn: async (data: AutoRechargeLimitForm) => {
      const response = await billingService.setupAutoRecharge(data, accountId)
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().balance(accountId, conversionRate),
      })
    },
  })
}

export const useAutoRechargeDetailsMutation = () => {
  const accountId = getAccountId()
  const { conversionRate } = useConversionRate()
  return useMutation({
    mutationFn: async (data: {
      autoRechargePayload: AutoRechargeLimitForm
      updateAuditLogsPayload: UpdateAuditLogsPayload
    }) => {
      const [autoRechargeResponse, updateAuditLogsResponse] = await Promise.all([
        billingService.setupAutoRecharge(data.autoRechargePayload, accountId),
        billingService.updateAuditLogs(data.updateAuditLogsPayload),
      ])
      return {
        autoRechargeResponse,
        updateAuditLogsResponse,
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().balance(accountId, conversionRate),
      })
      successToast({
        title: 'Success',
        description: 'Auto recharge details updated successfully',
      })
      track(AnalyticsEvents.AUTO_RECHARGE_UPDATED, {
        enabled: variables.autoRechargePayload.auto_recharge,
        was_enabled: variables.updateAuditLogsPayload.prev_state.auto_recharge,
        threshold_amount: variables.autoRechargePayload.threshold_amount,
        topup_amount: variables.autoRechargePayload.topup_amount,
      })
    },
    onError: () => {
      errorToast({
        title: 'Error',
        description: 'Unable to update some of the auto recharge details',
      })
    },
  })
}

export const useRechargeMutation = () =>
  useMutation({
    mutationFn: async (data: RechargePayload) => {
      const response = await billingService.recharge(data)
      return response
    },
  })

export const useRedirectionUrlMutation = () => {
  const accountId = getAccountId()
  return useMutation({
    mutationFn: async (data: { cardMappingId: number; accountId: number }) => {
      const response = await billingService.getRedirectionUrl(data.cardMappingId, data.accountId)
      if (response.success && response.redirection_url) {
        window.location.href = response.redirection_url
      }
      if (!response.success && response.card_state === CardState.REMOVED) {
        queryClient.invalidateQueries({
          queryKey: billingQueryKeys().cards(data.accountId),
        })
        errorToast({
          title: 'Error',
          description: 'Card removed',
        })
        // Resolved (non-thrown) 3DS removal — mirror the onError removal branch
        // so both outcomes are counted.
        track(AnalyticsEvents.PAYMENT_FAILED, {
          flow: '3ds_card',
          card_state: 'removed',
          error_message: response.error,
        })
      }
      return response
    },
    onError: (error: AxiosError) => {
      if (error.response?.data?.card_state === CardState.REMOVED) {
        queryClient.invalidateQueries({
          queryKey: billingQueryKeys().cards(accountId),
        })
        errorToast({
          title: 'Error',
          description: 'Card removed',
        })
        trackPaymentFailed('3ds_card', error, { card_state: 'removed' })
      } else if (error.response?.data?.card_state === CardState.ACTIVE) {
        queryClient.invalidateQueries({
          queryKey: billingQueryKeys().cards(accountId),
        })
        successToast({
          title: 'Success',
          description: 'Card added',
        })
        // Card added via the 3DS redirect path — the sibling non-3DS success
        // sites already fire CARD_ADDED, so this closes the undercount.
        track(AnalyticsEvents.CARD_ADDED, { method_type: PaymentMethod.CARD, flow: '3ds' })
      }
    },
  })
}

export const useDeleteCardMutation = () => {
  const accountId = getAccountId()
  return useMutation({
    mutationFn: async (data: DeleteCardPayload) => {
      const response = await billingService.deleteCard(data, accountId)
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().cards(accountId),
      })
      successToast({
        title: 'Success',
        description: 'Card deleted successfully',
      })
    },
    onError: () => {
      errorToast({
        title: 'Error',
        description: 'Failed to delete card',
      })
    },
  })
}

export const useUpdateDefaultCardMutation = () => {
  const accountId = getAccountId()
  return useMutation({
    mutationFn: async (data: UpdateDefaultCardPayload) => {
      const response = await billingService.updateDefaultCard(data, accountId)
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().cards(accountId),
      })
      successToast({
        title: 'Success',
        description: 'Default card updated successfully',
      })
    },
    onError: () => {
      errorToast({
        title: 'Error',
        description: 'Failed to update default card',
      })
    },
  })
}

export const useChangePlanMutation = () => {
  const accountId = getAccountId()
  const { conversionRate } = useConversionRate()
  return useMutation({
    mutationFn: async (data: ChangePlanPayload) => {
      const response = await hodorService.changePlan(data)
      return response
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getSettingsQueryKeys().profile,
      })
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().balance(accountId, conversionRate),
      })
      // Poll profile to pick up backend plan changes (backend updates async)
      for (const delay of [5000, 10000, 20000]) {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: getSettingsQueryKeys().profile,
          })
        }, delay)
      }
      // Only fire on upgrades — downgrades are a separate product event and
      // much rarer; conflating them would pollute upgrade funnel metrics.
      if (variables.change_type === 'upgrade') {
        track(AnalyticsEvents.PLAN_UPGRADED, {
          plan_name: variables.new_plan,
          previous_plan: variables.old_plan,
          amount: variables.amount,
          currency: variables.currency,
        })
      }
    },
  })
}

// ============================================
// ACH / Bank Direct Debit Mutations
// ============================================

export const useAddBankAccountMutation = () => {
  const accountId = getAccountId()
  return useMutation({
    mutationFn: async (data: AddBankAccountPayload) => {
      const response = await billingService.addBankAccount(data)
      return response
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().bankAccount(accountId),
      })
      successToast({
        title: 'Bank account added',
        description:
          'Your bank account was submitted for verification. You will receive two microdeposits with unique six-character reference codes for verification.',
      })
      track(AnalyticsEvents.BANK_ACCOUNT_ADDED, {
        account_type: variables.account_type,
        account_holder_type: variables.account_holder_type,
      })
    },
    onError: () => {
      errorToast({
        title: 'Error',
        description: 'Failed to add bank account. Please try again.',
      })
    },
  })
}

export const useDeleteBankAccountMutation = () => {
  const accountId = getAccountId()
  return useMutation({
    mutationFn: async (data: DeleteBankAccountPayload) => {
      const response = await billingService.deleteBankAccount(data)
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().bankAccount(accountId),
      })
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().achStatus(accountId),
      })
      successToast({
        title: 'Success',
        description: 'Bank account deleted successfully.',
      })
    },
    onError: () => {
      errorToast({
        title: 'Error',
        description: 'Failed to delete bank account. There may be pending transactions.',
      })
    },
  })
}

export const useVerifyBankAccountMutation = () => {
  const accountId = getAccountId()
  return useMutation({
    mutationFn: async (data: VerifyBankAccountPayload) => {
      const response = await billingService.verifyBankAccount(data)
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().bankAccount(accountId),
      })
      successToast({
        title: 'Success',
        description: 'Bank account verified successfully.',
      })
    },
    onError: () => {
      errorToast({
        title: 'Verification failed',
        description: 'Invalid verification code. Please check and try again.',
      })
    },
  })
}

export const useToggleACHMutation = () => {
  const accountId = getAccountId()
  return useMutation({
    mutationFn: async (data: { accountId: number; payload: ToggleACHPayload }) => {
      const response = await billingService.toggleACH(data.accountId, data.payload)
      return response
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().achStatus(accountId),
      })
      successToast({
        title: 'Success',
        description: response.enabled ? 'ACH auto-debit enabled.' : 'ACH auto-debit disabled.',
      })
      track(AnalyticsEvents.ACH_TOGGLED, { enabled: response.enabled })
    },
    onError: () => {
      errorToast({
        title: 'Error',
        description: 'Failed to update ACH status.',
      })
    },
  })
}

export const useAddScheduleMutation = () => {
  const accountId = getAccountId()
  return useMutation({
    mutationFn: async (data: AddSchedulePayload) => {
      const response = await billingService.addAutoDebitSchedule(data)
      return response
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().autoDebitSchedules(accountId, variables.bank_mapping_id),
      })
      successToast({
        title: 'Success',
        description: 'Auto-debit schedule created successfully.',
      })
    },
    onError: () => {
      errorToast({
        title: 'Error',
        description: 'Failed to create schedule. A schedule may already exist for this day.',
      })
    },
  })
}

export const useUpdateScheduleMutation = () => {
  const accountId = getAccountId()
  return useMutation({
    mutationFn: async (data: { scheduleId: string; payload: UpdateSchedulePayload }) => {
      const response = await billingService.updateAutoDebitSchedule(data.scheduleId, data.payload)
      return response
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().autoDebitSchedules(
          accountId,
          variables.payload.bank_mapping_id,
        ),
      })
      successToast({
        title: 'Success',
        description: 'Schedule updated successfully.',
      })
    },
    onError: () => {
      errorToast({
        title: 'Error',
        description: 'Failed to update schedule.',
      })
    },
  })
}

export const useDeleteScheduleMutation = () => {
  const accountId = getAccountId()
  return useMutation({
    mutationFn: async (data: {
      scheduleId: string
      bankMappingId: number
      actor: string
      client: string
    }) => {
      const response = await billingService.deleteAutoDebitSchedule(data.scheduleId, {
        account_id: accountId,
        actor: data.actor,
        client: data.client,
      })
      return { ...response, bankMappingId: data.bankMappingId }
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: billingQueryKeys().autoDebitSchedules(accountId, response.bankMappingId),
      })
      successToast({
        title: 'Success',
        description: 'Schedule deleted successfully.',
      })
    },
    onError: () => {
      errorToast({
        title: 'Error',
        description: 'Failed to delete schedule.',
      })
    },
  })
}
